import { z } from 'zod';

// Thread membership roles
export enum ThreadMemberRoleEnum {
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
}

// Thread visibility types
export enum ThreadVisibilityEnum {
  ORGANIZATION = 'ORGANIZATION', // All org members can see/join
  PRIVATE = 'PRIVATE', // Only invited members can see/join
}

// Thread membership status
export enum ThreadMemberStatusEnum {
  ACTIVE = 'ACTIVE',
  PENDING = 'PENDING', // Invited but not yet accepted
  REMOVED = 'REMOVED',
}

// Labels and descriptions for roles
export const THREAD_ROLE_LABELS: Record<ThreadMemberRoleEnum, string> = {
  [ThreadMemberRoleEnum.ADMIN]: 'Admin',
  [ThreadMemberRoleEnum.MEMBER]: 'Member',
};

export const THREAD_ROLE_DESCRIPTIONS: Record<ThreadMemberRoleEnum, string> = {
  [ThreadMemberRoleEnum.ADMIN]: 'Can manage members and thread settings',
  [ThreadMemberRoleEnum.MEMBER]: 'Can participate in discussions',
};

export const THREAD_ROLE_PROPERTIES: Record<ThreadMemberRoleEnum, { readonly?: boolean }> = {
  [ThreadMemberRoleEnum.ADMIN]: {},
  [ThreadMemberRoleEnum.MEMBER]: {},
};

// Visibility labels and descriptions
export const THREAD_VISIBILITY_LABELS: Record<ThreadVisibilityEnum, string> = {
  [ThreadVisibilityEnum.ORGANIZATION]: 'Organization-wide',
  [ThreadVisibilityEnum.PRIVATE]: 'Private',
};

export const THREAD_VISIBILITY_DESCRIPTIONS: Record<ThreadVisibilityEnum, string> = {
  [ThreadVisibilityEnum.ORGANIZATION]: 'All organization members can join automatically',
  [ThreadVisibilityEnum.PRIVATE]: 'Only invited members can participate',
};

// Thread member schema
export const ThreadMemberSchema = z.object({
  id: z.string().uuid(),
  threadId: z.string().uuid(),
  userId: z.string().uuid(),
  role: z.nativeEnum(ThreadMemberRoleEnum),
  status: z.nativeEnum(ThreadMemberStatusEnum),
  invitedById: z.string().uuid().nullable().optional(),
  invitedAt: z.date().nullable().optional(),
  joinedAt: z.date().nullable().optional(),
  lastReadAt: z.date().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type ThreadMember = z.infer<typeof ThreadMemberSchema>;

// Create thread member schema
export const CreateThreadMemberSchema = ThreadMemberSchema.omit({
  id: true,
  threadId: true,
  createdAt: true,
  updatedAt: true,
  joinedAt: true,
}).extend({
  invitedById: z.string().uuid().optional(),
});

export type CreateThreadMember = z.infer<typeof CreateThreadMemberSchema>;

// Update thread member schema
export const UpdateThreadMemberSchema = CreateThreadMemberSchema.partial();

export type UpdateThreadMember = z.infer<typeof UpdateThreadMemberSchema>;

// Thread member with user info
export const ThreadMemberWithUserSchema = ThreadMemberSchema.extend({
  user: z.object({
    id: z.string().uuid(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    displayName: z.string().nullable(),
    image: z.string().nullable(),
  }),
});

export type ThreadMemberWithUser = z.infer<typeof ThreadMemberWithUserSchema>;

// Thread member response schemas
export const ThreadMemberResponseSchema = z.object({
  data: ThreadMemberSchema,
  message: z.string().optional(),
});

export type ThreadMemberResponse = z.infer<typeof ThreadMemberResponseSchema>;

export const ThreadMembersResponseSchema = z.object({
  data: z.array(ThreadMemberWithUserSchema),
  total: z.number().optional(),
  message: z.string().optional(),
});

export type ThreadMembersResponse = z.infer<typeof ThreadMembersResponseSchema>;

// Thread settings schema (for visibility and auto-add settings)
export const ThreadSettingsSchema = z.object({
  id: z.string().uuid(),
  threadId: z.string().uuid(),
  visibility: z.nativeEnum(ThreadVisibilityEnum),
  autoAddNewMembers: z.boolean().default(false),
  allowSelfJoin: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type ThreadSettings = z.infer<typeof ThreadSettingsSchema>;

// Create thread settings schema
export const CreateThreadSettingsSchema = ThreadSettingsSchema.omit({
  id: true,
  threadId: true,
  createdAt: true,
  updatedAt: true,
});

export type CreateThreadSettings = z.infer<typeof CreateThreadSettingsSchema>;

// Update thread settings schema
export const UpdateThreadSettingsSchema = CreateThreadSettingsSchema.partial();

export type UpdateThreadSettings = z.infer<typeof UpdateThreadSettingsSchema>;
